import { FirebaseError, initializeApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  OAuthProvider,
  User,
  UserCredential,
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  deleteUser,
  signOut as firebaseSignOut,
  getAuth,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup
} from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useAuthStore, type AuthTokens } from '../stores/authStore.js';
import { clearSessionCookie, createSessionCookie } from './api.js';

const firebaseConfig = {
  apiKey: "AIzaSyD9u0iUQsa3cdifQF-A_pe9ptnhmSbmvms",
  authDomain: "squid-ca2b6.firebaseapp.com",
  projectId: "squid-ca2b6",
  storageBucket: "squid-ca2b6.firebasestorage.app",
  messagingSenderId: "602962558715",
  appId: "1:602962558715:web:bdcc6d09d7f6bb8da47a5e",
  measurementId: "G-0DZ11KK4QZ"
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
// Get Firebase auth instance
const auth = getAuth();

// Provider instances
const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');

/**
 * Log in a user with email and password
 */
export const signIn = async (email: string, password: string): Promise<AuthTokens> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // Check if email is verified
    if (!userCredential.user.emailVerified) {
      throw new Error('Please verify your email before signing in. Check your inbox for the verification email.');
    }

    // Create session cookie
    const idToken = await userCredential.user.getIdToken();
    await createSessionCookie(idToken);

    return mapFirebaseUserToTokens(userCredential);
  } catch (error) {
    if (error instanceof FirebaseError) {
      const errorMessage = getAuthErrorMessage(error);
      throw new Error(errorMessage);
    }

    // Re-throw email verification errors
    if (error instanceof Error && error.message.includes('verify your email')) {
      throw error;
    }

    throw error;
  }
};

/**
 * Initiate log in with Google
 */
export const signInWithGoogle = async (): Promise<AuthTokens | null> => {
  const userCredential = await signInWithPopup(auth, googleProvider);
  return mapFirebaseUserToTokens(userCredential);

};

/**
 * Initiate log in with Microsoft
 */
export const signInWithMicrosoft = async (): Promise<AuthTokens | null> => {
  const userCredential = await signInWithPopup(auth, microsoftProvider);
  return mapFirebaseUserToTokens(userCredential);
};

/**
 * OAuth signin with email validation for staff accounts
 */
export const signInWithGoogleWithValidation = async (): Promise<{ tokens: AuthTokens; email: string; isNewUser: boolean }> => {
  const userCredential = await signInWithPopup(auth, googleProvider);
  const tokens = await mapFirebaseUserToTokens(userCredential);
  const email = userCredential.user.email;
  const isNewUser = userCredential.user.metadata.creationTime === userCredential.user.metadata.lastSignInTime;

  if (!email) {
    throw new Error('No email found in Google account');
  }

  return { tokens, email, isNewUser };
};

/**
 * OAuth signin with email validation for staff accounts
 */
export const signInWithMicrosoftWithValidation = async (): Promise<{ tokens: AuthTokens; email: string; isNewUser: boolean }> => {
  const userCredential = await signInWithPopup(auth, microsoftProvider);
  const tokens = await mapFirebaseUserToTokens(userCredential);
  const email = userCredential.user.email;
  const isNewUser = userCredential.user.metadata.creationTime === userCredential.user.metadata.lastSignInTime;

  if (!email) {
    throw new Error('No email found in Microsoft account');
  }
  return { tokens, email, isNewUser };
};

/**
 * Sign up a new user with email and password
 */
export const signUp = async (email: string, password: string): Promise<{ userId: string }> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Send email verification
    await sendEmailVerification(userCredential.user);

    return { userId: userCredential.user.uid };
  } catch (error) {
    // Handle specific errors
    if (error instanceof FirebaseError) {
      const errorMessage = getAuthErrorMessage(error);
      throw new Error(errorMessage);
    }

    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
    // Clear auth store
    useAuthStore.getState().clearAuth();
  } catch (error) {
    if (error instanceof FirebaseError) {
      const errorMessage = getAuthErrorMessage(error);
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * Confirm email verification with code
 */
export const confirmSignUp = async (email: string, code: string): Promise<void> => {
  try {
    await applyActionCode(auth, code);
  } catch (error) {
    if (error instanceof FirebaseError) {
      const errorMessage = getAuthErrorMessage(error);
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * Resend verification email to the user
 */
export const resendConfirmationCode = async (): Promise<void> => {
  try {
    // User must be logged in to resend verification
    if (!auth.currentUser) {
      throw new Error('User must be logged in to resend verification');
    }

    await sendEmailVerification(auth.currentUser);
  } catch (error) {
    if (error instanceof FirebaseError) {
      const errorMessage = getAuthErrorMessage(error);
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * Send password reset email
 */
export const forgotPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    if (error instanceof FirebaseError) {
      const errorMessage = getAuthErrorMessage(error);
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * Reset password with code
 */
export const resetPassword = async (code: string, newPassword: string): Promise<void> => {
  try {
    await confirmPasswordReset(auth, code, newPassword);
  } catch (error) {
    if (error instanceof FirebaseError) {
      const errorMessage = getAuthErrorMessage(error);
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * Helper function to convert Firebase user to AuthTokens
 */
export const mapFirebaseUserToTokens = async (userCredential: UserCredential): Promise<AuthTokens> => {
  const user = userCredential.user;

  // Get ID token with refresh
  const idToken = await user.getIdToken(true);

  // Firebase doesn't expose refresh tokens directly to JS SDK
  // We'll set an expiry time of 1 hour from now (typical Firebase token lifetime)
  const expiresAt = Date.now() + 3600 * 1000;

  return {
    accessToken: idToken,
    idToken: idToken,
    refreshToken: user.refreshToken,
    expiresAt: expiresAt
  };
};

/**
 * Get current user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Setup auth state listener to keep tokens in sync
 */
export const setupAuthListener = (): (() => void) => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    // Mark as initialized after first auth state change

    if (user) {
      // User is signed in
      try {
        // Get fresh tokens
        const idToken = await user.getIdToken(true);
        const expiresAt = Date.now() + 3600 * 1000;

        // Create/refresh session cookie
        await createSessionCookie(idToken);

        // Update tokens in store
        useAuthStore.getState().setTokens({
          accessToken: idToken,
          idToken: idToken,
          refreshToken: user.refreshToken,
          expiresAt: expiresAt,
        });
        useAuthStore.getState().setInitialized(true);
      } catch (error) {
        console.error('Error refreshing token in auth listener:', error);
        // If session creation fails, clear auth state
        useAuthStore.getState().clearAuth();
      }
    } else {
      // Check if we have a cookie - only clear if we find __session
      const cookie = document.cookie;
      if (cookie.includes('__session')) {
        // User is signed out
        try {
          // Clear session cookie when user signs out
          await clearSessionCookie();
        } catch (error) {
          console.error('Error clearing session cookie in auth listener:', error);
        }
      }

      useAuthStore.getState().clearAuth();
    }
  });

  return unsubscribe;
};

/**
 * Check if current user's email is verified
 */
export const isEmailVerified = (): boolean => {
  const user = getCurrentUser();
  return user?.emailVerified ?? false;
};

/**
 * Hook that provides Firebase auth state with initialization tracking
 */
export const useFirebaseAuth = () => {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const isInitialized = useAuthStore((state) => state.isInitialized);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });

    return unsubscribe;
  }, []);

  return {
    user,
    isInitialized,
    isAuthenticated: !!user && isInitialized,
    isEmailVerified: user?.emailVerified ?? false,
    isCheckingAuth: !isInitialized
  };
};

/**
 * Get auth-related error messages
 */
const getAuthErrorMessage = (error: FirebaseError): string => {
  const errorCode = error.code;

  // Map Firebase error codes to user-friendly messages
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email is already in use';
    case 'auth/invalid-email':
      return 'Invalid email address';
    case 'auth/user-disabled':
      return 'This account has been disabled';
    case 'auth/user-not-found':
      return 'User not found';
    case 'auth/wrong-password':
      return 'Incorrect password';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters';
    case 'auth/invalid-verification-code':
      return 'Invalid verification code';
    case 'auth/invalid-verification-id':
      return 'Invalid verification ID';
    case 'auth/requires-recent-login':
      return 'This operation requires recent authentication. Please log in again.';
    case 'auth/expired-action-code':
      return 'This verification link has expired';
    case 'auth/invalid-action-code':
      return 'Invalid verification code';
    default:
      return error.message || 'Authentication error occurred';
  }
};

/**
 * Delete the current Firebase user account
 */
export const deleteCurrentUserAccount = async (): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    // Delete the user account
    await deleteUser(user);

    // Clear auth store and session
    useAuthStore.getState().clearAuth();
    await clearSessionCookie();
  } catch (error) {
    if (error instanceof FirebaseError) {
      const errorMessage = getAuthErrorMessage(error);
      throw new Error(errorMessage);
    }

    throw error;
  }
};

/**
 * Sign out and delete account helper for magic link email mismatches
 */
export const signOutAndDeleteAccount = async (): Promise<void> => {
  try {
    // First try to delete the account
    await deleteCurrentUserAccount();
  } catch (error) {
    // If deletion fails (e.g., requires recent login), just sign out
    console.warn('Failed to delete account, signing out instead:', error);
    await signOut();
  }
};

// Convert Firebase error codes to user-friendly messages
export const getFirebaseErrorMessage = (error: unknown): string => {
  if (!(error instanceof Error)) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Extract Firebase error code from error message
  const errorMessage = error.message.toLowerCase();

  // Check for Firebase error codes
  if (errorMessage.includes('auth/user-not-found')) {
    return 'No account found with this email address. Please check your email or sign up.';
  }

  if (errorMessage.includes('auth/wrong-password') || errorMessage.includes('auth/invalid-credential')) {
    return 'Incorrect password. Please try again or reset your password.';
  }

  if (errorMessage.includes('auth/invalid-email')) {
    return 'Please enter a valid email address.';
  }

  if (errorMessage.includes('auth/user-disabled')) {
    return 'This account has been disabled. Please contact support for assistance.';
  }

  if (errorMessage.includes('auth/too-many-requests')) {
    return 'Too many failed attempts. Please wait a moment before trying again.';
  }

  if (errorMessage.includes('auth/email-already-in-use')) {
    return 'An account with this email already exists. Please log in instead.';
  }

  if (errorMessage.includes('auth/weak-password')) {
    return 'Password is too weak. Please choose a stronger password with at least 8 characters.';
  }

  if (errorMessage.includes('auth/operation-not-allowed')) {
    return 'This sign-in method is not available. Please try a different method.';
  }

  if (errorMessage.includes('auth/network-request-failed')) {
    return 'Network error. Please check your connection and try again.';
  }

  if (errorMessage.includes('auth/popup-closed-by-user')) {
    return 'Sign-in was cancelled. Please try again.';
  }

  if (errorMessage.includes('auth/cancelled-popup-request')) {
    return 'Sign-in was cancelled. Please try again.';
  }

  if (errorMessage.includes('auth/requires-recent-login')) {
    return 'For security reasons, please log in again to delete your account.';
  }

  if (errorMessage.includes('auth/account-exists-with-different-credential')) {
    return 'An account with this email already exists. Please try a different log in method.';
  }

  // Check for email verification related errors
  if (errorMessage.includes('verify your email') ||
    errorMessage.includes('email before signing in') ||
    errorMessage.includes('not verified') ||
    errorMessage.includes('verification')) {
    return 'Please verify your email before signing in. Check your inbox for the verification email.';
  }

  // Default fallback
  return error.message || 'An unexpected error occurred. Please try again.';
};

/**
 * Delete current user account after OAuth validation failure
 * This is used when a user signs in with OAuth but their email domain is not allowed for staff accounts
 */
export const deleteAccountAfterValidationFailure = async (user?: User): Promise<void> => {
  try {
    const currentUser = user || getCurrentUser();
    if (currentUser) {
      await deleteUser(currentUser);
    }
    // Clear auth state
    useAuthStore.getState().clearAuth();
    await clearSessionCookie();
  } catch (error) {
    console.error('Failed to delete account after validation failure:', error);
    // If deletion fails, at least sign out
    await signOut();
  }
}; 